'use client';
import Availability from '@/entities/Availability/ui/Availability';
import { AddToCartButton } from '@/features/add-to-cart-button';
import { AddToFavoriteButton } from '@/features/add-to-favorite-button';
import { IProductCart } from '@/shared/api/getProduct/types';
import { ICity } from '@/shared/api/getShops/types';
import { useCityStore } from '@/shared/store/cityStore';
import { useFavoritesStore } from '@/shared/store/favoritesStore';
import { Container } from '@/shared/ui/Container';
import { Link } from '@/shared/ui/Link';
import { Title } from '@/shared/ui/Title';
import { EMPTY_HREF } from '@/shared/utils/consts';
import { formatCurrency } from '@/shared/utils/formatters';
import { twMergeExt } from '@/shared/utils/twMergeExt';
import { typograf } from '@/shared/utils/typograf';
import { wordEnding } from '@/shared/utils/wordEnding';
import StoresMap from '@/widgets/StoresMap/ui/StoresMap';
import { cva } from 'class-variance-authority';
import { AnimatePresence } from 'framer-motion';
import { FC, useMemo, useRef, useState } from 'react';
// import StickyBox from 'react-sticky-box';
import BonusesIcon from '~/icons/bonuses.svg';
import CorrectIcon from '~/icons/correct.svg';
import DiscountIcon from '~/icons/discount.svg';
import StarIcon from '~/icons/star-2.svg';

interface Props {
  className?: string;
  vendorCode?: number;
  brand?: string;
  rating?: number | null;
  reviewsCount?: number;
  title: string;
  volume?: string;
  weight?: string;
  descr?: string;
  price?: number;
  discountPrice?: number;
  id: number;
  isFavorite?: boolean;
  data: IProductCart;
}

export const DetailsForm: FC<Props> = ({
  className,
  vendorCode,
  brand,
  rating,
  reviewsCount,
  title,
  volume,
  weight,
  descr,
  price,
  discountPrice,
  id,
  data,
}) => {
  const ref = useRef<HTMLDivElement>(null);

  const [showStoresMap, setShowStoresMap] = useState<boolean>(false);
  const city = useCityStore((state) => state.city);
  const cityName = useCityStore((state) => state.cityName);

  const defaultCity = data?.amount?.find((i) => i.city.code === 'default');
  const defaultShop = defaultCity?.store.find((i) => i.store.uf_mainstore_delivery);

  const cityShops = useMemo(() => {
    if (!city) return defaultCity ? [defaultCity] : [];

    return data?.amount?.find((i) => i.city.code === city)?.store || [];
  }, [city]);

  const favoriteProducts = useFavoritesStore((state) => state.favoriteProducts);

  const isFav = favoriteProducts?.includes(id);

  const defaultShopAmount = useMemo(() => {
    return Math.max(defaultShop?.amount || 0, 0);
  }, [defaultShop]);

  const shopsAmount = useMemo(() => {
    const storesAvailable = cityShops.filter((i) => i.amount > 0) || [];

    if (storesAvailable.length > 0) {
      return [
        {
          name: cityName,
          coordinates: storesAvailable[0].store.map,
          totalAmount: storesAvailable.reduce((acc, i) => acc + i.amount, 0),
          maxAmount: storesAvailable.reduce((acc, i) => {
            return Math.max(acc, i.amount);
          }, 0),
          shops: storesAvailable.map((i) => ({
            ...i.store,
            amount: i.amount,
          })),
        },
      ];
    }

    return [
      {
        name: cityName,
        coordinates: '',
        totalAmount: 0,
        maxAmount: 0,
        shops: [],
      },
    ];
  }, [city, cityName]);

  const maxAmount = useMemo(() => {
    if (!city) {
      return defaultShopAmount;
    }

    if (shopsAmount.length && shopsAmount[0].totalAmount < 1) {
      return 0;
    }

    return shopsAmount[0].maxAmount || defaultShopAmount || 0;
  }, [shopsAmount, defaultShopAmount]);

  return (
    <>
      <Container ref={ref} className={twMergeExt(cvaRoot(), className)} variant={'small'}>
        {vendorCode ? <div className={cvaVendorCode()}>Артикул: {vendorCode}</div> : null}

        <div className={'flex items-center gap-1.2'}>
          {brand ? (
            <Link
              className={'text-bodyMSemibold text-cMediumGreen'}
              href={process.env.NEXT_PUBLIC_FRONT_BASE_URL + `/brands/${brand.split(' ').join('-').toLowerCase()}/`}
              prefetch={false}
            >
              {brand}
            </Link>
          ) : null}
          <div className={'flex items-center gap-0.8'}>
            {rating ? (
              <div className={'flex items-center gap-0.4 text-bodyMSemibold text-cBlack'}>
                <StarIcon className={'w-1.2 h-1.2'} />
                <div>{rating.toFixed(1)}</div>
              </div>
            ) : null}
            {reviewsCount ? (
              <div className={'text-bodyM text-cBlack48'}>
                {reviewsCount} {wordEnding(reviewsCount, ['отзыв', 'отзыва', 'отзывов'])}
              </div>
            ) : null}
            <div></div>
          </div>
        </div>

        <h1 className={cvaH1()}>{title}</h1>
        <div className="flex flex-col gap-0.2 items-start">
          <Title trigger={true} className={cvaHeading()}>
            <div>{typograf.execute(title)}</div>
          </Title>
          <span className={' font-base text-bodyM text-cBlack48'}>
            {volume}
            {volume && weight ? '   ' : null}
            {weight}
          </span>
        </div>

        {descr ? <div className={'mt-2.4 text-bodyM text-cBlack48'}>{descr}</div> : null}

        <div className={'flex flex-col gap-0.4 mt-2.4'}>
          {maxAmount && cityName !== 'unknown' ? (
            <div className={'flex items-center gap-0.4'}>
              <CorrectIcon className={'w-1.6 h-1.6 text-cMediumGreen'} />
              <div className={'text-bodyMSemibold text-cMediumGreen'}>Есть доставка</div>
            </div>
          ) : null}

          {shopsAmount?.length > 0 && shopsAmount[0].totalAmount && cityName !== 'unknown' ? (
            <div className={'flex items-center gap-0.4'}>
              <CorrectIcon className={'w-1.6 h-1.6 text-cMediumGreen'} />
              <div className={'inline-flex text-bodyMSemibold text-cMediumGreen '}>
                В наличии 
                <Link
                  wrapperClassName={
                    'border-dashed border-b border-cMediumGreen hover:text-cDarkGreen hover:border-cDarkGreen transition-all duration-300'
                  }
                  hoverEffect={'color'}
                  href={EMPTY_HREF}
                  onClick={(e) => {
                    e.preventDefault();
                    setShowStoresMap(true);
                  }}
                >
                  {`в ${shopsAmount[0]?.shops.length} ${wordEnding(shopsAmount[0]?.shops.length, [
                    'магазине',
                    'магазинах',
                    'магазинах',
                  ])}`}
                </Link>
              </div>
            </div>
          ) : null}
          <Availability value={maxAmount} />
        </div>

        {price ? (
          <div className={cvaPriceContainer()}>
            <div className={cvaPrice()}>
              {formatCurrency(discountPrice ? discountPrice : price)}

              {discountPrice ? (
                <div className={cvaDiscountLabelContainer()}>
                  <DiscountIcon className={'w-4.6 h-2.5 text-cExtraOrange'} />
                  <div className={cvaDiscountPercent()}>-{((1 - discountPrice / price) * 100).toFixed(0)}%</div>
                </div>
              ) : null}
            </div>
            <div className={cvaDiscountPriceContainer()}>
              {discountPrice ? <div className={cvaDiscountPrice()}>{formatCurrency(price)}</div> : null}

              <div className={cvaBonuses()}>
                <span className={'text-cBlack48 text-bodyM'}>Вернем бонусами</span> + {Math.round(price * 0.01)}{' '}
                <BonusesIcon className={'inline-block w-2.4 h-2.4 ml-0.4'} />
              </div>
            </div>
          </div>
        ) : null}

        <div className={'flex gap-1.2 pt-1.6 pr-1.9 md:pb-8.8 pb-0 md:bg-cLightGrey bg-transparent'}>
          <div className={'flex flex-col gap-1.6 w-full'}>
            <AddToCartButton
              className={'flex-1'}
              size={'large'}
              id={id}
              disabled={!maxAmount}
              totalAmount={maxAmount}
            />
            {/* <InstallmentPlan
            className={'max-w-[33.9rem]'}
          /> */}
          </div>
          <div className={'flex flex-col gap-1.6 '}>
            <AddToFavoriteButton size={'large'} active={isFav} id={id} />
          </div>
        </div>
      </Container>
      <AnimatePresence>
        {showStoresMap ? (
          <StoresMap
            onClose={() => setShowStoresMap(false)}
            activeCity={cityName}
            shops={shopsAmount as ICity[]}
            typeI="availability"
            selectedProduct={data}
          />
        ) : null}
      </AnimatePresence>
    </>
  );
};

const cvaRoot = cva([
  'ProductDetailForm-cvaRoot',
  'relative',
  'md:grow-0 md:shrink-0 md:w-[44rem] lg:w-[62.6rem]',
  'pt-6.5 rounded-[6.4rem] md:px-4.2 lg:pl-8.8 lg:pr-[11.2rem]',
  'text-bodyM',
  'md:bg-cLightGrey bg-transparent',
]);

const cvaVendorCode = cva(['DetailsForm-cvaVendorCode', 'absolute top-2.4 right-5.6', 'text-bodyXS text-cBlack48']);

const cvaHeading = cva(['DetailsForm-cvaHeading', 'mt-2.4', 'font-secondary text-h3']);

const cvaH1 = cva(['DetailsForm-cvaH1', 'hidden']);

const cvaPriceContainer = cva(['DetailsForm-cvaPriceContainer', ' flex flex-col items-start', 'mt-2.4']);

const cvaPrice = cva(['DetailsForm-cvaPrice', 'relative', 'font-secondary text-h3']);

const cvaDiscountPriceContainer = cva([
  'DetailsForm-cvaDiscountPriceContainer',
  'relative',
  'flex justify-start items-center gap-1.2',
  'mt-0.2',
]);

const cvaDiscountPrice = cva([
  'DetailsForm-cvaDiscountPrice',
  'relative',
  'text-cBlack48',
  'before:absolute before:top-1/2 before:-left-0.2',
  'before:w-[calc(100%+0.4rem)] before:h-0.1',
  'before:bg-cExtraOrange before:-translate-y-1/2 before:-rotate-[12deg]',
]);

const cvaDiscountLabelContainer = cva([
  'DetailsForm-cvaDiscountLabelContainer',
  'absolute top-0 left-[calc(100%+1.2rem)]',
]);

const cvaDiscountPercent = cva([
  'DetailsForm-cvaDiscountPercent',
  'absolute top-[calc(50%+0.1rem)] left-[calc(50%-0.3rem)] -translate-x-1.2 -translate-y-1/2',
  'text-cWhite text-bodyM font-base',
]);

const cvaBonuses = cva(['DetailsForm-cvaBonuses', 'text-cMediumGreen text-bodyMSemibold']);
